import React from 'react'
import {
  type LayoutChangeEvent,
  type StyleProp,
  StyleSheet,
  Text as RNText,
  View,
  type ViewStyle,
} from 'react-native'
import Animated, {
  useAnimatedStyle,
  useReducedMotion,
  useSharedValue,
  withSpring,
  withTiming,
} from 'react-native-reanimated'

import {web} from '#/alf'

export type FloatingTabBarItemBase = {
  key: string
  isActive: boolean
}

type RenderItemArgs<T extends FloatingTabBarItemBase> = {
  item: T
  index: number
  isActive: boolean
  onLayout: (event: LayoutChangeEvent) => void
}

export type FloatingTabBarProps<T extends FloatingTabBarItemBase> = {
  items: T[]
  renderItem: (args: RenderItemArgs<T>) => React.ReactNode
  bottomOffset: number
  webBottomOffset?: string
  containerStyle?: StyleProp<ViewStyle>
  pillStyle?: StyleProp<ViewStyle>
  highlightStyle?: StyleProp<ViewStyle>
  onPillLayout?: (height: number) => void
  renderBackground?: () => React.ReactNode
  containerProps?: Omit<
    React.ComponentProps<typeof Animated.View>,
    'style' | 'children' | 'onLayout'
  >
  debug?: boolean
}

const HIGHLIGHT_HEIGHT = 48
const HIGHLIGHT_MIN_WIDTH = 56
const HIGHLIGHT_INSET = 4
const SPRING_CONFIG = {
  damping: 24,
  stiffness: 240,
  mass: 0.9,
  overshootClamping: true,
}

export function FloatingTabBar<T extends FloatingTabBarItemBase>({
  items,
  renderItem,
  bottomOffset,
  webBottomOffset,
  containerStyle,
  pillStyle,
  highlightStyle,
  onPillLayout,
  renderBackground,
  containerProps,
  debug = false,
}: FloatingTabBarProps<T>) {
  const reducedMotion = useReducedMotion()
  const layoutsRef = React.useRef<Array<{x: number; width: number}>>([])
  const [layoutVersion, setLayoutVersion] = React.useState(0)

  const highlightX = useSharedValue(0)
  const highlightWidth = useSharedValue(0)
  const highlightOpacity = useSharedValue(0)

  const activeIndex = React.useMemo(() => {
    const index = items.findIndex(item => item.isActive)
    return index === -1 ? 0 : index
  }, [items])

  const onItemLayout = React.useCallback(
    (index: number) => (event: LayoutChangeEvent) => {
      const {x, width} = event.nativeEvent.layout
      const prev = layoutsRef.current[index]
      if (!prev || prev.x !== x || prev.width !== width) {
        layoutsRef.current[index] = {x, width}
        setLayoutVersion(version => version + 1)
      }
    },
    [],
  )

  React.useEffect(() => {
    const layout = layoutsRef.current[activeIndex]
    if (!layout) {
      highlightOpacity.set(() => withTiming(0, {duration: 120}))
      return
    }

    const targetWidth = Math.max(
      layout.width - HIGHLIGHT_INSET * 2,
      HIGHLIGHT_MIN_WIDTH,
    )
    const targetX = layout.x + (layout.width - targetWidth) / 2

    if (reducedMotion) {
      highlightX.set(targetX)
      highlightWidth.set(targetWidth)
      highlightOpacity.set(1)
      return
    }

    highlightX.set(() => withSpring(targetX, SPRING_CONFIG))
    highlightWidth.set(() => withSpring(targetWidth, SPRING_CONFIG))
    highlightOpacity.set(() => withTiming(1, {duration: 180}))
  }, [
    activeIndex,
    layoutVersion,
    reducedMotion,
    highlightOpacity,
    highlightWidth,
    highlightX,
  ])

  const animatedHighlightStyle = useAnimatedStyle(() => ({
    opacity: highlightOpacity.get(),
    transform: [{translateX: highlightX.get()}],
    width: highlightWidth.get(),
  }))

  const showDebug = debug && __DEV__

  return (
    <Animated.View
      {...containerProps}
      style={[
        styles.container,
        {bottom: bottomOffset},
        webBottomOffset
          ? web({
              bottom: webBottomOffset,
              position: 'fixed',
              left: 'env(safe-area-inset-left)',
              right: 'env(safe-area-inset-right)',
            })
          : web({
              position: 'fixed',
              left: 'env(safe-area-inset-left)',
              right: 'env(safe-area-inset-right)',
            }),
        containerStyle,
      ]}>
      <View
        style={[
          styles.pillOuter,
          web({boxShadow: '0 10px 30px rgba(0,0,0,0.22)'}),
        ]}
        onLayout={event => {
          onPillLayout?.(event.nativeEvent.layout.height)
        }}>
        <View
          style={[
            styles.pillInner,
            web({
              backdropFilter: 'blur(18px)',
              WebkitBackdropFilter: 'blur(18px)',
            }),
            pillStyle,
          ]}>
          {renderBackground?.()}

          <Animated.View
            pointerEvents="none"
            style={[styles.highlight, highlightStyle, animatedHighlightStyle]}
          />

          {items.map((item, index) => (
            <React.Fragment key={item.key}>
              {renderItem({
                item,
                index,
                isActive: item.isActive,
                onLayout: onItemLayout(index),
              })}
            </React.Fragment>
          ))}

          {showDebug
            ? layoutsRef.current.map((layout, index) => {
                if (!layout) return null
                return (
                  <View
                    key={`debug-${index}`}
                    pointerEvents="none"
                    style={[
                      styles.debugBox,
                      {left: layout.x, width: layout.width},
                    ]}>
                    <RNText style={styles.debugText}>
                      {Math.round(layout.x)} / {Math.round(layout.width)}
                    </RNText>
                  </View>
                )
              })
            : null}
        </View>
      </View>
    </Animated.View>
  )
}

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    left: 0,
    right: 0,
    alignItems: 'center',
    zIndex: 120,
  },
  pillOuter: {
    width: '92%',
    maxWidth: 420,
    height: 64,
    borderRadius: 999,
    shadowColor: '#000',
    shadowOpacity: 0.18,
    shadowRadius: 18,
    shadowOffset: {width: 0, height: 10},
    elevation: 12,
  },
  pillInner: {
    ...StyleSheet.absoluteFillObject,
    borderRadius: 999,
    overflow: 'hidden',
    padding: 8,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderWidth: StyleSheet.hairlineWidth,
  },
  highlight: {
    position: 'absolute',
    top: 8,
    left: 0,
    height: HIGHLIGHT_HEIGHT,
    borderRadius: 999,
    borderWidth: StyleSheet.hairlineWidth,
  },
  debugBox: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    borderWidth: 1,
    borderColor: 'rgba(255,0,0,0.45)',
    justifyContent: 'flex-end',
    paddingBottom: 4,
  },
  debugText: {
    fontSize: 10,
    color: 'rgba(255,0,0,0.7)',
    textAlign: 'center',
  },
})
